import type { MapDocument, MapLocation } from "../shared/types";
import {
  mapStandHeight,
  projectMapDepth,
  projectMapPoint,
  type VenueMapCamera,
} from "./venueMapProjection";

type Point = { x: number; y: number };
type FacePoint = Point & { depth: number };
const EPSILON = 1e-7;

function cross(a: Point, b: Point, c: Point) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function inside(point: Point, polygon: Point[]) {
  let contained = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i],
      b = polygon[j];
    if (
      Math.abs(cross(a, b, point)) < EPSILON &&
      point.x >= Math.min(a.x, b.x) - EPSILON &&
      point.x <= Math.max(a.x, b.x) + EPSILON &&
      point.y >= Math.min(a.y, b.y) - EPSILON &&
      point.y <= Math.max(a.y, b.y) + EPSILON
    )
      return true;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    )
      contained = !contained;
  }
  return contained;
}

/** Reciprocal eye distance is affine across each projected planar face. */
function faceDepthAtPoint(point: Point, face: FacePoint[]): number | undefined {
  if (!inside(point, face)) return undefined;
  const origin = face[0];
  let first = origin,
    second = origin,
    determinant = 0;
  for (let index = 1; index < face.length - 1; index++) {
    const value = cross(origin, face[index], face[index + 1]);
    if (Math.abs(value) > Math.abs(determinant)) {
      determinant = value;
      first = face[index];
      second = face[index + 1];
    }
  }
  // Degenerate walls occur when looking directly along their plane; they
  // cover no pixels and must not intercept another visible face.
  if (Math.abs(determinant) < EPSILON) return undefined;
  const firstWeight = cross(origin, point, second) / determinant;
  const secondWeight = cross(origin, first, point) / determinant;
  return (
    origin.depth +
    firstWeight * (first.depth - origin.depth) +
    secondWeight * (second.depth - origin.depth)
  );
}

/** Pick the nearest visible roof or wall drawn by either renderer. */
export function mapLocationAtPoint(
  document: MapDocument,
  camera: VenueMapCamera,
  point: Point,
): MapLocation | undefined {
  const { geometry } = document;
  const locations = new Map(
    document.locations.map((location) => [location.id, location]),
  );
  let selected: MapLocation | undefined;
  let hitStand = false;
  let nearestDepth = -Infinity;
  for (const stand of geometry.stands) {
    if (stand.hidden) continue;
    const z = mapStandHeight(stand);
    const anchor = {
      x: stand.label.x / geometry.width,
      y: stand.label.y / geometry.height,
    };
    const project = ([x, y]: number[], height: number): FacePoint => {
      const world = {
        x: x / geometry.width,
        y: y / geometry.height,
        z: height,
      };
      return {
        ...projectMapPoint(camera, world),
        depth: projectMapDepth(camera, world),
      };
    };
    let depth = -Infinity;
    const consider = (face: FacePoint[]) => {
      const candidate = faceDepthAtPoint(point, face);
      if (candidate !== undefined) depth = Math.max(depth, candidate);
    };
    for (const polygon of stand.polygons) {
      const roof = polygon.map((position) => project(position, z));
      consider(roof);
      if (z <= 0) continue;
      const base = polygon.map((position) => project(position, 0));
      for (let index = 0; index < polygon.length; index++) {
        const next = (index + 1) % polygon.length;
        consider([base[index], base[next], roof[next], roof[index]]);
      }
    }
    if (!Number.isFinite(depth) || depth < nearestDepth) continue;
    nearestDepth = depth;
    hitStand = true;
    const location = locations.get(stand.id);
    selected = location?.hidden
      ? undefined
      : location || {
          id: stand.id,
          label: [stand.id, stand.name].filter(Boolean).join(" · "),
          x: anchor.x,
          y: anchor.y,
        };
  }
  // A visible solid with a hidden location still occludes booths and entrance
  // markers behind it; do not synthesize a selectable location for that roof.
  if (hitStand) return selected;
  for (const entrance of geometry.entrances) {
    if (locations.get(entrance.id)?.hidden) continue;
    const location = locations.get(entrance.id) || {
      id: entrance.id,
      label: entrance.label,
      x: entrance.x / geometry.width,
      y: entrance.y / geometry.height,
    };
    const anchor = projectMapPoint(camera, location);
    if (Math.hypot(point.x - anchor.x, point.y - anchor.y) < 16)
      return location;
  }
  return undefined;
}
